import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const K = {
  bg: '#f8fafc', card: '#ffffff', rand: '#e2e8f0', tekst: '#0f172a', zacht: '#64748b',
  groen: '#16a34a', oranje: '#ea580c', rood: '#dc2626', blauw: '#2563eb', grijs: '#94a3b8', donker: '#0f172a'
};
const SOORTEN = { apk: { label: 'APK-keuring', icon: '🔍' }, onderhoud: { label: 'Onderhoudsbeurt', icon: '🔧' }, banden: { label: 'Banden', icon: '🛞' }, schade: { label: 'Schade/reparatie', icon: '🛠️' } };
const vandaagIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const nlDag = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
const nlDatum = d => d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const isoDatum = d => d ? String(d).slice(0, 10) : '';

function statusVan(v, grens) {
  if (!v.actief) return { key: 'inactief', label: 'Inactief', kleur: K.grijs };
  if (v.dagen == null) return { key: 'onbekend', label: 'APK onbekend', kleur: K.grijs };
  if (v.dagen <= 0) return { key: 'rood', label: v.dagen === 0 ? 'Verloopt vandaag' : `Verlopen (${-v.dagen} d)`, kleur: K.rood };
  if (v.dagen <= grens) return { key: 'oranje', label: `Nog ${v.dagen} dagen`, kleur: K.oranje };
  return { key: 'groen', label: `Nog ${v.dagen} dagen`, kleur: K.groen };
}

const knop = (extra = {}) => ({
  padding: '8px 14px', borderRadius: 8, border: `1px solid ${K.rand}`, background: K.card, color: K.tekst,
  fontSize: 14, fontWeight: 600, cursor: 'pointer', ...extra
});
const invoer = { width: '100%', padding: '9px 11px', borderRadius: 8, border: `1px solid ${K.rand}`, fontSize: 14, color: K.tekst, background: '#fff', boxSizing: 'border-box' };
const labelStijl = { display: 'block', fontSize: 12, fontWeight: 600, color: K.zacht, margin: '12px 0 4px' };

function Kenteken({ k }) {
  return <span style={{ display: 'inline-block', background: '#facc15', border: `2px solid ${K.donker}`, borderRadius: 5, padding: '2px 8px', fontFamily: 'Arial Black, Arial, sans-serif', fontWeight: 900, fontSize: 13, letterSpacing: '.06em', color: K.donker, whiteSpace: 'nowrap' }}>{k}</span>;
}
function Badge({ s }) {
  return <span style={{ display: 'inline-block', background: s.kleur + '1a', color: s.kleur, fontSize: 12, fontWeight: 700, padding: '4px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.label}</span>;
}
function Modal({ titel, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: K.card, color: K.tekst, borderRadius: 14, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{titel}</h3>
          <button onClick={onClose} style={{ border: 0, background: 'none', fontSize: 22, cursor: 'pointer', color: K.zacht }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Wagenpark() {
  const [params, setParams] = useSearchParams();
  const [afdelingen, setAfdelingen] = useState([]);
  const [afd, setAfd] = useState('');
  const [data, setData] = useState({ voertuigen: [], instellingen: null, leden: [] });
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState('');
  const [melding, setMelding] = useState('');
  const [zoek, setZoek] = useState('');
  const [filter, setFilter] = useState('alle');
  const [nieuw, setNieuw] = useState(null);
  const [bewerk, setBewerk] = useState(null);
  const [instOpen, setInstOpen] = useState(false);
  const [inst, setInst] = useState(null);
  const [importRes, setImportRes] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [tab, setTab] = useState('voertuigen');
  const [afspraken, setAfspraken] = useState([]);
  const [plan, setPlan] = useState(null);
  const fileRef = useRef();

  const toon = m => { setMelding(m); setTimeout(() => setMelding(''), 4000); };
  const err = e => setFout(e?.response?.data?.error || e.message || 'Er ging iets mis');

  useEffect(() => {
    api.get('/wagenpark/afdelingen').then(r => {
      setAfdelingen(r.data);
      setAfd(a => a || (r.data[0] ? String(r.data[0].id) : ''));
    }).catch(err);
  }, []);

  const herlaad = useCallback(() => {
    if (!afd) return;
    setLaden(true);
    api.get('/wagenpark', { params: { afdeling_id: afd } })
      .then(r => { setData(r.data); setFout(''); })
      .catch(err).finally(() => setLaden(false));
    api.get('/wagenpark/afspraken/lijst', { params: { afdeling_id: afd } }).then(r => setAfspraken(r.data)).catch(() => {});
  }, [afd]);
  useEffect(() => { herlaad(); }, [herlaad]);

  // ?voertuig=ID (link uit de herinneringsmail) → direct bewerken
  useEffect(() => {
    const id = params.get('voertuig');
    if (!id || !data.voertuigen.length) return;
    const v = data.voertuigen.find(x => String(x.id) === id);
    if (v) { setBewerk({ ...v, afspraak_datum: isoDatum(v.afspraak_datum) }); params.delete('voertuig'); setParams(params, { replace: true }); }
  }, [params, data.voertuigen, setParams]);

  const grens = useMemo(() => Math.max(...((data.instellingen?.herinner_dagen) || [30])), [data.instellingen]);
  const lijst = useMemo(() => data.voertuigen.map(v => ({ ...v, s: statusVan(v, grens) })), [data.voertuigen, grens]);
  const tellers = useMemo(() => {
    const t = { rood: 0, oranje: 0, groen: 0, gepland: 0 };
    lijst.forEach(v => { if (t[v.s.key] != null) t[v.s.key]++; });
    return t;
  }, [lijst]);
  const zichtbaar = lijst.filter(v =>
    (filter === 'alle' || (filter === 'gepland' ? heeftAfspraak(v) : v.s.key === filter)) &&
    (!zoek || [v.kenteken, v.merk, v.model, v.bestuurder_naam, v.notitie].join(' ').toLowerCase().includes(zoek.toLowerCase()))
  );

  const komend = useMemo(() => afspraken.filter(a => a.status === 'gepland' && a.datum >= vandaagIso()), [afspraken]);
  const volgendeVan = id => komend.find(a => a.voertuig_id === id);
  const apkAfspraak = v => (v.afspraak_datum && isoDatum(v.afspraak_datum) >= vandaagIso()) ? isoDatum(v.afspraak_datum) : null;
  const heeftAfspraak = v => Boolean(volgendeVan(v.id) || apkAfspraak(v));
  const nieuwePlan = v => setPlan({ voertuig_id: v.id, kenteken: v.kenteken, soort: 'onderhoud', datum: '', tijd: '', garage: data.instellingen?.garage_naam || '', notitie: '' });
  const opslaanPlan = async () => {
    setBezig(true);
    try {
      if (plan.id) await api.put(`/wagenpark/afspraken/${plan.id}`, plan);
      else await api.post(`/wagenpark/${plan.voertuig_id}/afspraken`, plan);
      toon(plan.id ? 'Afspraak bijgewerkt' : `Afspraak ingepland voor ${plan.kenteken} — push verstuurd`);
      setPlan(null); setBewerk(null); herlaad();
    } catch (e) { err(e); } finally { setBezig(false); }
  };
  const zetStatus = async (a, status) => {
    if (status === 'geannuleerd' && !window.confirm(`Afspraak ${a.kenteken} op ${nlDag(a.datum)} annuleren?`)) return;
    try { await api.put(`/wagenpark/afspraken/${a.id}`, { status }); toon(status === 'afgerond' ? 'Afgerond' : 'Geannuleerd'); herlaad(); } catch (e) { err(e); }
  };
  const verwijderAfspraak = async a => {
    if (!window.confirm('Afspraak definitief verwijderen?')) return;
    try { await api.delete(`/wagenpark/afspraken/${a.id}`); toon('Afspraak verwijderd'); setPlan(null); herlaad(); } catch (e) { err(e); }
  };

  const opslaanNieuw = async () => {
    setBezig(true);
    try {
      const r = await api.post('/wagenpark', { ...nieuw, afdeling_id: afd });
      toon(r.data.rdw_gevonden ? `${r.data.voertuig.kenteken} toegevoegd — ${r.data.voertuig.merk || ''} ${r.data.voertuig.model || ''}` : `${r.data.voertuig.kenteken} toegevoegd, maar niet gevonden bij RDW`);
      setNieuw(null); herlaad();
    } catch (e) { err(e); } finally { setBezig(false); }
  };
  const opslaanBewerk = async () => {
    setBezig(true);
    try { await api.put(`/wagenpark/${bewerk.id}`, { ...bewerk, afspraak_datum: null }); toon('Opgeslagen'); setBewerk(null); herlaad(); }
    catch (e) { err(e); } finally { setBezig(false); }
  };
  const verwijder = async v => {
    if (!window.confirm(`${v.kenteken} verwijderen uit het wagenpark?`)) return;
    try { await api.delete(`/wagenpark/${v.id}`); toon(`${v.kenteken} verwijderd`); setBewerk(null); herlaad(); } catch (e) { err(e); }
  };
  const rdwCheck = async v => {
    try { await api.post(`/wagenpark/${v.id}/rdw`); toon(`${v.kenteken} bijgewerkt via RDW`); herlaad(); } catch (e) { err(e); }
  };
  const importeer = async e => {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    const fd = new FormData(); fd.append('bestand', f); fd.append('afdeling_id', afd);
    setBezig(true); setImportRes(null);
    try { const r = await api.post('/wagenpark/import', fd, { timeout: 300000 }); setImportRes(r.data); herlaad(); }
    catch (er) { err(er); } finally { setBezig(false); }
  };
  const voorbeeld = async () => {
    try {
      const r = await api.get('/wagenpark/voorbeeld', { params: { afdeling_id: afd }, responseType: 'blob' });
      const naam = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = naam ? naam[1] : 'wagenpark.xlsx'; a.click();
    } catch (e) { err(e); }
  };
  const openInst = () => {
    const i = data.instellingen || {};
    setInst({ ...i, herinner_dagen: (i.herinner_dagen || [30]).join(', ') });
    setInstOpen(true);
  };
  const opslaanInst = async () => {
    setBezig(true);
    try { await api.put(`/wagenpark/instellingen/${afd}`, inst); toon('Instellingen opgeslagen'); setInstOpen(false); herlaad(); }
    catch (e) { err(e); } finally { setBezig(false); }
  };

  const Tegel = ({ k, label, n, kleur }) => (
    <button onClick={() => setFilter(filter === k ? 'alle' : k)} style={{
      flex: '1 1 130px', textAlign: 'left', padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
      background: K.card, border: `1px solid ${filter === k ? kleur : K.rand}`, boxShadow: filter === k ? `0 0 0 2px ${kleur}33` : 'none'
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: kleur }}>{n}</div>
      <div style={{ fontSize: 13, color: K.zacht, fontWeight: 600 }}>{label}</div>
    </button>
  );

  const i = data.instellingen;
  return (
    <div style={{ padding: '20px 16px', maxWidth: 1200, margin: '0 auto', color: K.tekst }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>🚐 Wagenpark</h1>
          <div style={{ color: K.zacht, fontSize: 14, marginTop: 2 }}>Kentekens en APK — datums automatisch via RDW</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {afdelingen.length > 1 && (
            <select value={afd} onChange={e => setAfd(e.target.value)} style={{ ...invoer, width: 'auto' }}>
              {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
            </select>
          )}
          <button style={knop()} onClick={openInst}>⚙️ Instellingen</button>
          <button style={knop()} onClick={voorbeeld} title="Huidige lijst als Excel — aanpassen en weer importeren">📤 Excel downloaden</button>
          <button style={knop()} onClick={() => fileRef.current.click()} disabled={bezig}>📥 Excel importeren</button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={importeer} style={{ display: 'none' }} />
          <button style={knop({ background: K.donker, color: '#fff', border: 0 })} onClick={() => setNieuw({ kenteken: '', bestuurder_id: '', notitie: '' })}>+ Voertuig</button>
        </div>
      </div>

      {fout && <div onClick={() => setFout('')} style={{ background: '#fef2f2', color: K.rood, border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 10, marginBottom: 12, cursor: 'pointer' }}>{fout}</div>}
      {melding && <div style={{ background: '#f0fdf4', color: K.groen, border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 10, marginBottom: 12 }}>{melding}</div>}
      {bezig && importRes == null && <div style={{ color: K.zacht, fontSize: 14, marginBottom: 12 }}>Bezig… (bij import wordt elk kenteken bij de RDW opgezocht)</div>}

      {importRes && (
        <div style={{ background: K.card, border: `1px solid ${K.rand}`, borderRadius: 12, padding: 16, marginBottom: 16, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <b>Import klaar</b><button onClick={() => setImportRes(null)} style={{ border: 0, background: 'none', cursor: 'pointer', color: K.zacht }}>×</button>
          </div>
          <div style={{ marginTop: 6 }}>✅ {importRes.toegevoegd.length} toegevoegd · ✏️ {importRes.bijgewerkt.length} bijgewerkt</div>
          {importRes.onbekende_bestuurder.length > 0 && <div style={{ color: K.oranje, marginTop: 4 }}>Bestuurder niet gevonden: {importRes.onbekende_bestuurder.join(', ')}</div>}
          {importRes.andere_afdeling.length > 0 && <div style={{ color: K.oranje, marginTop: 4 }}>Staat bij een andere afdeling (overgeslagen): {importRes.andere_afdeling.join(', ')}</div>}
          {importRes.niet_bij_rdw.length > 0 && <div style={{ color: K.oranje, marginTop: 4 }}>Niet gevonden bij RDW: {importRes.niet_bij_rdw.join(', ')}</div>}
          {importRes.ongeldig.length > 0 && <div style={{ color: K.rood, marginTop: 4 }}>Ongeldig: {importRes.ongeldig.join(', ')}</div>}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <Tegel k="rood" label="Verlopen" n={tellers.rood} kleur={K.rood} />
        <Tegel k="oranje" label={`Binnen ${grens} dagen`} n={tellers.oranje} kleur={K.oranje} />
        <Tegel k="gepland" label="Afspraak gepland" n={lijst.filter(heeftAfspraak).length} kleur={K.blauw} />
        <Tegel k="groen" label="In orde" n={tellers.groen} kleur={K.groen} />
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${K.rand}`, marginBottom: 14 }}>
        {[['voertuigen', `Voertuigen (${data.voertuigen.length})`], ['afspraken', `Afspraken (${komend.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '9px 14px', border: 0, background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            color: tab === k ? K.tekst : K.zacht, borderBottom: `3px solid ${tab === k ? K.donker : 'transparent'}`, marginBottom: -1 }}>{l}</button>
        ))}
      </div>

      {tab === 'afspraken' && (
        <div style={{ background: K.card, border: `1px solid ${K.rand}`, borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: K.zacht, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {['Datum', 'Kenteken', 'Soort', 'Garage / notitie', 'Status', ''].map(h => <th key={h} style={{ padding: '12px 14px', borderBottom: `1px solid ${K.rand}`, fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {afspraken.length === 0 && <tr><td colSpan={6} style={{ padding: 28, color: K.zacht, textAlign: 'center' }}>Nog geen afspraken. Klik op een voertuig → "Afspraak plannen".</td></tr>}
              {[...afspraken].sort((x, y) => (x.datum < vandaagIso()) - (y.datum < vandaagIso()) || (x.datum < vandaagIso() ? y.datum.localeCompare(x.datum) : x.datum.localeCompare(y.datum))).map(a => {
                const verleden = a.datum < vandaagIso();
                const sk = { gepland: [K.blauw, 'Gepland'], afgerond: [K.groen, 'Afgerond'], geannuleerd: [K.grijs, 'Geannuleerd'] }[a.status] || [K.grijs, a.status];
                return (
                  <tr key={a.id} style={{ borderTop: `1px solid ${K.rand}`, opacity: a.status === 'geannuleerd' || (verleden && a.status !== 'gepland') ? .55 : 1 }}>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap', fontWeight: 600 }}>{nlDag(a.datum)}{a.tijd && <span style={{ color: K.zacht, fontWeight: 400 }}> · {a.tijd}</span>}</td>
                    <td style={{ padding: '11px 14px' }}><Kenteken k={a.kenteken} /></td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>{SOORTEN[a.soort]?.icon} {SOORTEN[a.soort]?.label || a.soort}</td>
                    <td style={{ padding: '11px 14px' }}>{a.garage || <span style={{ color: K.grijs }}>—</span>}{a.notitie && <div style={{ fontSize: 12, color: K.zacht }}>{a.notitie}</div>}</td>
                    <td style={{ padding: '11px 14px' }}><Badge s={{ kleur: verleden && a.status === 'gepland' ? K.oranje : sk[0], label: verleden && a.status === 'gepland' ? 'Afronden?' : sk[1] }} /></td>
                    <td style={{ padding: '11px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {a.status === 'gepland' && <>
                        <button onClick={() => zetStatus(a, 'afgerond')} style={knop({ padding: '5px 9px', fontSize: 12, marginRight: 4 })} title="Afgerond">✓</button>
                        <button onClick={() => setPlan({ ...a, tijd: a.tijd || '' })} style={knop({ padding: '5px 9px', fontSize: 12, marginRight: 4 })}>Wijzig</button>
                        <button onClick={() => zetStatus(a, 'geannuleerd')} style={knop({ padding: '5px 9px', fontSize: 12, color: K.rood })}>Annuleer</button>
                      </>}
                      {a.status !== 'gepland' && <button onClick={() => verwijderAfspraak(a)} style={knop({ padding: '5px 9px', fontSize: 12, color: K.zacht })}>Verwijder</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'voertuigen' && <>
      <input placeholder="Zoek op kenteken, merk, bestuurder…" value={zoek} onChange={e => setZoek(e.target.value)} style={{ ...invoer, marginBottom: 12 }} />

      <div style={{ background: K.card, border: `1px solid ${K.rand}`, borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: K.zacht, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              {['Kenteken', 'Voertuig', 'APK-datum', 'Status', 'Bestuurder', ''].map(h => <th key={h} style={{ padding: '12px 14px', borderBottom: `1px solid ${K.rand}`, fontWeight: 600 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {laden && <tr><td colSpan={6} style={{ padding: 24, color: K.zacht, textAlign: 'center' }}>Laden…</td></tr>}
            {!laden && zichtbaar.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 28, color: K.zacht, textAlign: 'center' }}>
                {data.voertuigen.length ? 'Geen voertuigen voor dit filter' : <>Nog geen voertuigen. Voeg er een toe of importeer een Excel-lijst (<a href="#voorbeeld" onClick={e => { e.preventDefault(); voorbeeld(); }}>voorbeeldbestand</a>).</>}
              </td></tr>
            )}
            {!laden && zichtbaar.map(v => (
              <tr key={v.id} onClick={() => setBewerk({ ...v, afspraak_datum: isoDatum(v.afspraak_datum) })} style={{ cursor: 'pointer', borderTop: `1px solid ${K.rand}`, opacity: v.actief ? 1 : .55 }}>
                <td style={{ padding: '11px 14px', borderLeft: `4px solid ${v.s.kleur}` }}><Kenteken k={v.kenteken} /></td>
                <td style={{ padding: '11px 14px' }}>
                  <div style={{ fontWeight: 600 }}>{[v.merk, v.model].filter(Boolean).join(' ') || <span style={{ color: K.zacht }}>Onbekend</span>}</div>
                  {v.notitie && <div style={{ fontSize: 12, color: K.zacht }}>{v.notitie}</div>}
                </td>
                <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>{nlDatum(v.apk_vervaldatum)}</td>
                <td style={{ padding: '11px 14px' }}>
                  <Badge s={v.s} />
                  {volgendeVan(v.id) ? (() => { const a = volgendeVan(v.id); return <div style={{ fontSize: 12, color: K.blauw, marginTop: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>{SOORTEN[a.soort]?.icon} {nlDag(a.datum)}{a.tijd ? ' ' + a.tijd : ''}</div>; })()
                    : apkAfspraak(v) && <div style={{ fontSize: 12, color: K.blauw, marginTop: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>📅 APK-afspraak {nlDag(apkAfspraak(v))}</div>}
                </td>
                <td style={{ padding: '11px 14px' }}>{v.bestuurder_naam || <span style={{ color: K.grijs }}>—</span>}</td>
                <td style={{ padding: '11px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button title="Opnieuw ophalen bij RDW" onClick={e => { e.stopPropagation(); rdwCheck(v); }} style={knop({ padding: '5px 9px', fontSize: 12 })}>↻ RDW</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </>}

      <div style={{ marginTop: 14, fontSize: 14, color: K.zacht }}>
        ✉️ APK-herinneringen ({((i && i.herinner_dagen) || [30]).join(', ')} dagen vooraf) gaan naar:{' '}
        {i && i.meld_email ? <b style={{ color: K.tekst }}>{i.meld_email}</b> : <span style={{ color: K.oranje }}>nog geen adres ingesteld</span>}
        {' '}· <a href="#instellingen" onClick={e => { e.preventDefault(); openInst(); }}>wijzigen</a>
      </div>

      {i && (i.garage_naam || i.garage_telefoon) && (
        <div style={{ marginTop: 14, fontSize: 14, color: K.zacht }}>
          Keuring bij <b style={{ color: K.tekst }}>{i.garage_naam || 'garage'}</b>
          {i.garage_telefoon && <> · <a href={'tel:' + i.garage_telefoon.replace(/[^\d+]/g, '')} style={{ color: K.tekst, fontWeight: 600 }}>📞 {i.garage_telefoon}</a></>}
          {i.garage_link && <> · <a href={i.garage_link} target="_blank" rel="noreferrer">website</a></>}
        </div>
      )}

      {nieuw && (
        <Modal titel="Voertuig toevoegen" onClose={() => setNieuw(null)}>
          <label style={labelStijl}>Kenteken</label>
          <input autoFocus value={nieuw.kenteken} onChange={e => setNieuw({ ...nieuw, kenteken: e.target.value.toUpperCase() })} placeholder="AB-123-C" style={{ ...invoer, fontWeight: 700, letterSpacing: '.05em' }}
            onKeyDown={e => e.key === 'Enter' && nieuw.kenteken && opslaanNieuw()} />
          <div style={{ fontSize: 12, color: K.zacht, marginTop: 4 }}>Merk, model en APK-datum worden automatisch opgehaald bij de RDW.</div>
          <label style={labelStijl}>Vaste bestuurder (optioneel)</label>
          <select value={nieuw.bestuurder_id} onChange={e => setNieuw({ ...nieuw, bestuurder_id: e.target.value })} style={invoer}>
            <option value="">— geen —</option>
            {data.leden.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
          </select>
          <label style={labelStijl}>Opmerking</label>
          <input value={nieuw.notitie} onChange={e => setNieuw({ ...nieuw, notitie: e.target.value })} style={invoer} placeholder="bijv. bus ploeg 1" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button style={knop()} onClick={() => setNieuw(null)}>Annuleren</button>
            <button style={knop({ background: K.donker, color: '#fff', border: 0 })} disabled={!nieuw.kenteken || bezig} onClick={opslaanNieuw}>{bezig ? 'RDW ophalen…' : 'Toevoegen'}</button>
          </div>
        </Modal>
      )}

      {bewerk && (
        <Modal titel={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Kenteken k={bewerk.kenteken} /> <span style={{ fontSize: 15, fontWeight: 600 }}>{[bewerk.merk, bewerk.model].filter(Boolean).join(' ')}</span></span>} onClose={() => setBewerk(null)}>
          <div style={{ display: 'flex', gap: 16, fontSize: 14, marginTop: 10, background: K.bg, padding: '10px 12px', borderRadius: 10 }}>
            <div><div style={{ color: K.zacht, fontSize: 12 }}>APK-vervaldatum</div><b>{nlDatum(bewerk.apk_vervaldatum)}</b></div>
            <div><div style={{ color: K.zacht, fontSize: 12 }}>RDW bijgewerkt</div>{bewerk.rdw_bijgewerkt ? nlDatum(bewerk.rdw_bijgewerkt) : 'nooit'}</div>
          </div>
          <div style={{ marginTop: 14, border: `1px solid ${K.rand}`, borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <b style={{ fontSize: 14 }}>Afspraken</b>
              <button style={knop({ padding: '6px 10px', fontSize: 13, background: K.donker, color: '#fff', border: 0 })} onClick={() => nieuwePlan(bewerk)}>+ Afspraak plannen</button>
            </div>
            {komend.filter(a => a.voertuig_id === bewerk.id).length === 0
              ? <div style={{ fontSize: 13, color: K.zacht, marginTop: 6 }}>Geen geplande afspraken.</div>
              : komend.filter(a => a.voertuig_id === bewerk.id).map(a => (
                <div key={a.id} onClick={() => setPlan({ ...a, tijd: a.tijd || '' })} style={{ fontSize: 13, marginTop: 6, cursor: 'pointer' }}>
                  {SOORTEN[a.soort]?.icon} <b>{nlDag(a.datum)}{a.tijd ? ' ' + a.tijd : ''}</b> — {SOORTEN[a.soort]?.label}{a.garage ? ` · ${a.garage}` : ''}
                </div>
              ))}
          </div>
          {i && i.garage_telefoon && <a href={'tel:' + i.garage_telefoon.replace(/[^\d+]/g, '')} style={{ display: 'inline-block', marginTop: 8, fontSize: 14, fontWeight: 600, color: K.tekst }}>📞 {i.garage_naam ? i.garage_naam + ' — ' : ''}{i.garage_telefoon}</a>}
          <label style={labelStijl}>Vaste bestuurder</label>
          <select value={bewerk.bestuurder_id || ''} onChange={e => setBewerk({ ...bewerk, bestuurder_id: e.target.value })} style={invoer}>
            <option value="">— geen —</option>
            {data.leden.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
          </select>
          <label style={labelStijl}>Extra e-mail voor deze auto</label>
          <input value={bewerk.meld_email || ''} onChange={e => setBewerk({ ...bewerk, meld_email: e.target.value })} style={invoer} placeholder="naast de afdelings-mail, komma-gescheiden" />
          <label style={labelStijl}>Opmerking</label>
          <input value={bewerk.notitie || ''} onChange={e => setBewerk({ ...bewerk, notitie: e.target.value })} style={invoer} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
            <input type="checkbox" checked={bewerk.actief !== false} onChange={e => setBewerk({ ...bewerk, actief: e.target.checked })} /> Actief (uitvinken = geen herinneringen)
          </label>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18 }}>
            <button style={knop({ color: K.rood })} onClick={() => verwijder(bewerk)}>Verwijderen</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={knop()} onClick={() => setBewerk(null)}>Annuleren</button>
              <button style={knop({ background: K.donker, color: '#fff', border: 0 })} disabled={bezig} onClick={opslaanBewerk}>Opslaan</button>
            </div>
          </div>
        </Modal>
      )}

      {plan && (
        <Modal titel={<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{plan.id ? 'Afspraak wijzigen' : 'Afspraak plannen'} <Kenteken k={plan.kenteken} /></span>} onClose={() => setPlan(null)}>
          <label style={labelStijl}>Soort</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {Object.entries(SOORTEN).map(([k, s2]) => (
              <button key={k} onClick={() => setPlan({ ...plan, soort: k })} style={knop({ textAlign: 'left', fontSize: 13,
                ...(plan.soort === k ? { background: K.donker, color: '#fff', borderColor: K.donker } : {}) })}>{s2.icon} {s2.label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 2 }}><label style={labelStijl}>Datum</label><input type="date" min={plan.id ? undefined : vandaagIso()} value={plan.datum} onChange={e => setPlan({ ...plan, datum: e.target.value })} style={invoer} /></div>
            <div style={{ flex: 1 }}><label style={labelStijl}>Tijd (optioneel)</label><input type="time" value={plan.tijd} onChange={e => setPlan({ ...plan, tijd: e.target.value })} style={invoer} /></div>
          </div>
          <label style={labelStijl}>Garage</label>
          <input value={plan.garage || ''} onChange={e => setPlan({ ...plan, garage: e.target.value })} style={invoer} />
          <label style={labelStijl}>Notitie</label>
          <input value={plan.notitie || ''} onChange={e => setPlan({ ...plan, notitie: e.target.value })} style={invoer} placeholder="bijv. grote beurt + remmen nakijken" />
          <div style={{ fontSize: 12, color: K.zacht, marginTop: 8 }}>Bestuurder en werkvoorbereiders krijgen een push bij inplannen en de dag ervoor. Zichtbaar in de Planning-kalender.</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18 }}>
            {plan.id ? <button style={knop({ color: K.rood })} onClick={() => zetStatus(plan, 'geannuleerd').then(() => setPlan(null))}>Annuleren</button> : <span />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={knop()} onClick={() => setPlan(null)}>Sluiten</button>
              <button style={knop({ background: K.donker, color: '#fff', border: 0 })} disabled={!plan.datum || bezig} onClick={opslaanPlan}>{plan.id ? 'Opslaan' : 'Inplannen'}</button>
            </div>
          </div>
        </Modal>
      )}

      {instOpen && inst && (
        <Modal titel="Instellingen wagenpark" onClose={() => setInstOpen(false)}>
          <label style={labelStijl}>Herinneren … dagen vóór APK-datum</label>
          <input value={inst.herinner_dagen} onChange={e => setInst({ ...inst, herinner_dagen: e.target.value })} style={invoer} placeholder="30  of  60, 30, 7" />
          <div style={{ fontSize: 12, color: K.zacht, marginTop: 4 }}>Meerdere momenten komma-gescheiden. Verlopen auto's krijgen dagelijks een melding.</div>
          <label style={labelStijl}>Herinnering mailen naar</label>
          <input value={inst.meld_email || ''} onChange={e => setInst({ ...inst, meld_email: e.target.value })} style={invoer} placeholder="naam@gvb.nl, collega@gvb.nl" />
          <div style={{ fontSize: 12, color: K.zacht, marginTop: 4 }}>Werkvoorbereiders van de afdeling en de vaste bestuurder krijgen altijd een push.</div>
          <div style={{ borderTop: `1px solid ${K.rand}`, margin: '18px 0 0', paddingTop: 6, fontWeight: 700, fontSize: 14 }}>Garage voor de keuring</div>
          <label style={labelStijl}>Naam</label>
          <input value={inst.garage_naam || ''} onChange={e => setInst({ ...inst, garage_naam: e.target.value })} style={invoer} />
          <label style={labelStijl}>Adres</label>
          <input value={inst.garage_adres || ''} onChange={e => setInst({ ...inst, garage_adres: e.target.value })} style={invoer} />
          <label style={labelStijl}>Telefoon</label>
          <input value={inst.garage_telefoon || ''} onChange={e => setInst({ ...inst, garage_telefoon: e.target.value })} style={invoer} />
          <label style={labelStijl}>Website (optioneel)</label>
          <input value={inst.garage_link || ''} onChange={e => setInst({ ...inst, garage_link: e.target.value })} style={invoer} placeholder="https://" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
            <a href="#voorbeeld" onClick={e => { e.preventDefault(); voorbeeld(); }} style={{ fontSize: 13 }}>Excel downloaden</a>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={knop()} onClick={() => setInstOpen(false)}>Annuleren</button>
              <button style={knop({ background: K.donker, color: '#fff', border: 0 })} disabled={bezig} onClick={opslaanInst}>Opslaan</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
